/**
 * `useUpdateStepPosition(workflowId)` — optimistic position-only step mutation
 * for the AC-1377 React Query migration (AC-1404 / Phase 3.3).
 *
 * Implements the contract from the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`:
 *   - Section 2.6 — mutation + invalidation contract
 *   - Section 5.3 — mutation hook signatures (optimistic variants)
 *   - Section 8.2 — Phase 3 verification table
 *
 * ## Why this exists (and why it's separate from `useUpdateStep`)
 *
 * Step drags are the highest-frequency mutation in the workflow editor. A
 * single drag can fire dozens of `position_x` / `position_y` updates per
 * second as the user moves a node, and a pessimistic round-trip (server
 * write → invalidate → refetch → re-render) introduces visible lag and
 * snap-back artefacts. The user perceives the drag as "instant" only if
 * the cache reflects the new position synchronously.
 *
 * `useUpdateStep` (AC-1403) is intentionally pessimistic — it covers
 * label / config / agent_id changes where the server is the source of
 * truth and a brief disabled state is acceptable. This hook is the
 * focused optimistic counterpart for the hot-path position field. The
 * underlying API call is the same (`apiService.updateWorkflowStep`); the
 * difference is the `onMutate` / `onError` / `onSettled` lifecycle that
 * mutates the cache before the server confirms.
 *
 * Consumer migration (WorkflowEditor) lives in AC-1406. This ticket is
 * additive only — both `useUpdateStep` and `useUpdateStepPosition` ship
 * side-by-side and consumers pick the right one.
 *
 * ## Optimistic-update lifecycle
 *
 * Standard TanStack Query "snapshot, optimistic, rollback, settle" pattern
 * (https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates):
 *
 *   1. `onMutate` is fired SYNCHRONOUSLY before the mutationFn runs.
 *      It must:
 *        a. `await queryClient.cancelQueries({ queryKey })` — prevents an
 *           in-flight refetch from clobbering our optimistic write the
 *           moment it lands. This is the single most common bug in
 *           hand-rolled optimistic updates; it is non-negotiable.
 *        b. Snapshot the current cache via `getQueryData`.
 *        c. Write the optimistic update via `setQueryData`.
 *        d. Return `{ previousSteps }` so the rollback path can restore.
 *
 *   2. `onError(err, vars, context)` restores from `context.previousSteps`.
 *      Also fires a toast so the user knows the drag did not persist.
 *
 *   3. `onSettled` invalidates the steps key. This runs on BOTH success
 *      and error paths and is what re-syncs the cache with server truth
 *      after the mutation lifecycle ends. On success it pulls the
 *      authoritative position back; on error it forces a fresh read so
 *      the cache cannot stay diverged from the server even if the rollback
 *      somehow drifted.
 *
 * ## Why we do NOT invalidate execution
 *
 * Position is purely cosmetic — it does not affect run results, step
 * ordering, or any field the execution panel renders. The pessimistic
 * `useUpdateStep` invalidates execution because labels / agent_id /
 * config changes can affect rendered execution rows; this hook is
 * deliberately position-only and skips that invalidation to avoid
 * thrashing execution refetches while the user drags.
 *
 * If a future change makes execution depend on position (unlikely), add
 * the second invalidation here AND extend the optimistic write to cover
 * the relevant execution fields (a partial optimistic update that leaves
 * other fields stale would be worse than no optimistic update at all).
 *
 * ## Rapid drag composition
 *
 * Multiple in-flight position mutations compose correctly because each
 * `onMutate` reads the current cache (which already reflects the
 * previous optimistic write) and writes the new position on top. The
 * `cancelQueries` call ensures no refetch lands between them. The final
 * `onSettled` invalidation ensures the server's truth wins once the
 * dust settles. Tests cover this composition explicitly.
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';

import apiService from '../../services/api';
import { WorkflowStep } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Position-update input. The stepId is the canonical string-form id from
 * the server (`orchestration_step_id`). Both `position_x` and `position_y`
 * are required — partial position updates are not a real use case (the
 * canvas always has both coordinates) and accepting partial values would
 * complicate the optimistic update with a merge step.
 */
export type UpdateStepPositionVariables = {
  stepId: string | number;
  position_x: number;
  position_y: number;
};

/**
 * Context returned from `onMutate` and consumed by `onError` for rollback.
 * Exported because callers writing additional `onError` / `onSuccess`
 * handlers via `mutate(vars, { onError })` may want to inspect the
 * snapshot.
 */
export type UpdateStepPositionContext = {
  /**
   * The step list as it existed before the optimistic write. `undefined`
   * if no `[workflow, id, steps]` cache entry existed at mutate time
   * (e.g. the workflow is being edited but no read-side subscriber has
   * populated the cache yet — rare but possible).
   */
  previousSteps: WorkflowStep[] | undefined;
};

/**
 * `useUpdateStepPosition(workflowId)` — optimistic mutation for
 * `PUT /api/v1/orchestrations/steps/:stepId` with a position-only payload.
 *
 * @param workflowId The workflow whose steps cache will be optimistically
 *                   updated. Captured in closure so the mutation always
 *                   targets the right workflow's cache slot.
 *
 * @example
 *   const updatePos = useUpdateStepPosition(workflowId);
 *   const onNodeDragStop = (_evt, node) => {
 *     updatePos.mutate({
 *       stepId: node.id,
 *       position_x: node.position.x,
 *       position_y: node.position.y,
 *     });
 *   };
 */
export function useUpdateStepPosition(
  workflowId: string | number,
): UseMutationResult<
  WorkflowStep,
  Error,
  UpdateStepPositionVariables,
  UpdateStepPositionContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowStep,
    Error,
    UpdateStepPositionVariables,
    UpdateStepPositionContext
  >({
    // The mutationFn sends ONLY the position fields. The server's update
    // endpoint accepts a partial WorkflowStep; sending more fields would
    // (a) waste bandwidth and (b) risk overwriting concurrent edits to
    // unrelated fields. AC-1406 will route label / config edits through
    // `useUpdateStep` and only position drags through this hook.
    mutationFn: ({ stepId, position_x, position_y }) =>
      apiService.updateWorkflowStep(stepId as string, {
        position_x,
        position_y,
      }),

    onMutate: async ({ stepId, position_x, position_y }) => {
      // Step 1: prevent in-flight refetches from clobbering our write.
      // Without this, a refetch that resolves AFTER setQueryData lands
      // would overwrite the optimistic position with stale server data.
      await queryClient.cancelQueries({
        queryKey: queryKeys.workflowSteps(workflowId),
      });

      // Step 2: snapshot the current cache for rollback. May be undefined
      // if the cache entry has not been populated yet — that's fine, the
      // rollback path handles undefined.
      const previousSteps = queryClient.getQueryData<WorkflowStep[]>(
        queryKeys.workflowSteps(workflowId),
      );

      // Step 3: write the optimistic update. Functional setter form so
      // we operate on the LATEST cache state (matters for rapid back-to-
      // back drags where multiple onMutate calls interleave). If the
      // cache is empty (`undefined`) we leave it alone — no synthetic
      // step row to write into.
      queryClient.setQueryData<WorkflowStep[] | undefined>(
        queryKeys.workflowSteps(workflowId),
        (oldSteps) => {
          if (!oldSteps) return oldSteps;
          return oldSteps.map((s) =>
            // The cache uses string-form ids; coerce both sides to string
            // for the comparison so callers can pass either form.
            String(s.orchestration_step_id) === String(stepId)
              ? { ...s, position_x, position_y }
              : s,
          );
        },
      );

      // Step 4: return the rollback context. RQ passes this to onError /
      // onSettled as the third argument.
      return { previousSteps };
    },

    onError: (_err, _vars, context) => {
      // Restore the snapshot. If `previousSteps` was undefined (cache was
      // empty at mutate time) we just clear the slot rather than leaving
      // the optimistic write in place.
      if (context) {
        queryClient.setQueryData(
          queryKeys.workflowSteps(workflowId),
          context.previousSteps,
        );
      }
      toast.error('Failed to save step position');
    },

    onSettled: () => {
      // Re-sync with server truth. Runs on BOTH success and error paths.
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowSteps(workflowId),
      });
    },
  });
}

export default useUpdateStepPosition;
